import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SubscriptionsRounded from '@mui/icons-material/SubscriptionsRounded';
import {
  Stack,
} from '@mui/material';
import { ORG_ID } from 'config';
import { ChannelState } from 'constants/channel-state.enum';
import Role from 'constants/role';
import { useHasRole } from 'hooks/useHasRole';
import jsonExport from 'jsonexport/dist';
import React from 'react';
import {
  ArrayField,
  ArrayInput,
  AutocompleteArrayInput,
  Create,
  CreateButton,
  Datagrid,
  DateField,
  DeleteButton,
  downloadCSV,
  Edit,
  EditButton,
  ExportButton,
  Filter,
  FilterButton,
  FormTab,
  List,
  NullableBooleanInput,
  NumberField,
  NumberInput,
  ReferenceManyField,
  Resource,
  SelectInput,
  Show,
  ShowButton,
  SimpleFormIterator,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar
} from 'react-admin';
import { channelFilterInputs } from 'shared-fields/ChannelFilterInputs';
import { CopyField } from 'shared-fields/CopyField';
import CustomDateInput from 'shared-fields/CustomDateInput';
import OrganizationReferenceField from 'shared-fields/OrganizationReferenceField';
import OrganizationReferenceInput from 'shared-fields/OrganizationReferenceInput';
import RangeField from 'shared-fields/RangeField';
import UserReferenceField from 'shared-fields/UserReferenceField';
import UserReferenceInput from 'shared-fields/UserReferenceInput';
import { generateSelectOptions } from 'utils/filters-utils';
import { rolesMatch } from 'utils/user';
import { ActivitiesListContent } from 'views/activities';
import { IncomeListContent } from 'views/income';
import { CategoriesField } from './CategoriesField';
import { TAGS } from './constants';
import { DescriptionField } from './DescriptionField';
import { DescriptionInput } from './DescriptionInput';
import { Health } from './Health';
import { Last30DaysTotalVideosField } from './Last30DaysTotalVideosField';
import { Link } from './Link';
import { SourceField } from './SourceField';
import { StateField, ICON_MAP as StateIconMap } from './StateField';
import { StatsTabContent } from './StatsTabContent';
import TagChipField from './TagChipField';
import { UTMField } from './UTMField';

const tagsChoices = Object.values(TAGS).map((value) => ({ id: value, name: value }));

function ChannelFilter(props) {
  return (
    <Filter {...props}>
      {channelFilterInputs}
    </Filter>
  );
}

const exporter = (channels) => {
  const channelsForExport = channels.map((ch) => ({
    ...ch,
    incomeSharing: undefined,
  }));
  jsonExport(channelsForExport, (err, csv) => {
    downloadCSV(csv, 'channels');
  });
};

function BulkActionButtons() {
  const hasRole = useHasRole();
  return hasRole(Role.CHANNEL_DELETE) && <DeleteButton />;
}

export function ChannelListContent(props) {
  const hasRole = useHasRole();

  return (
    <Datagrid {...props} bulkActionButtons={BulkActionButtons}>
      <CopyField source="id" label="ID" />
      <StateField />
      <TagChipField source="tags" label="Tags" />
      <CopyField
        source="title"
        label="Title"
        sx={{
          textAlign: 'right',
          justifyContent: 'flex-end',
        }}
      />
      <DescriptionField>
        <AssignmentRoundedIcon />
      </DescriptionField>
      <Link />
      <CopyField source="ownerTel" label="Tel" />
      <CopyField
        source="ownerName"
        label="Owner"
        sx={{
          textAlign: 'right',
        }}
      />
      {/* <AdSenseField /> */}
      <TextField source="type" />
      <RangeField source="averageIncome" label="Avg Inc" max={15000} palette="green" humanize />
      <RangeField source="subscriberCount" label="Subs" max={1000} palette="green" humanize />
      <RangeField source="stats.last30DaysStats.totalViews" label="Views" max={1000000} humanize />
      <Last30DaysTotalVideosField />
      <SourceField />
      <Health />
      <UserReferenceField />
      {!ORG_ID && <OrganizationReferenceField />}
      <DateField source="createdAt" label="Created" showTime locales="fa-IR" />
      <DateField source="acceptedAt" label="Accepted" showTime locales="fa-IR" />
      <DateField source="monetizedAt" label="Monetized" showTime locales="fa-IR" />
      <DateField source="syncedAt" label="Synced" showTime locales="fa-IR" />
      {
        hasRole(Role.CHANNEL_EDIT) && (
          <EditButton />
        )
      }

      <ShowButton />
    </Datagrid>
  );
}
export function ChannelList() {
  const hasRole = useHasRole();

  return (
    <List
      sort={{ field: 'id', order: 'DESC' }}
      filters={<ChannelFilter />}
      filterDefaultValues={{
        state: `!${ChannelState.REJECTED}`,
      }}
      exporter={exporter}
      perPage={25}
      actions={(
        <TopToolbar>
          <FilterButton filters={channelFilterInputs} />
          <ExportButton />
          {hasRole(Role.CHANNEL_CREATE) && <CreateButton />}
        </TopToolbar>
      )}
    >
      <ChannelListContent />
    </List>
  );
}

// function IncomeTabContent(props) {
//   return (
//     <ReferenceManyField
//       {...props}
//       perPage={100}
//       reference="incomes"
//       target="userId"
//       addLabel={false}
//     >
//       <IncomeListContent />
//     </ReferenceManyField>
//   );
// }

export function ChannelEdit(props) {
  const hasRole = useHasRole();

  return (
    <Edit mutationMode="optimistic" {...props}>
      <TabbedForm>
        <FormTab label="General">
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <TextInput source="id" disabled />
            <TextInput source="title" disabled />
          </Stack>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <TextInput source="ownerName" />
            <TextInput source="ownerTel" />
            {/* <TextInput source="ownerEmail" /> */}
          </Stack>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <SelectInput source="state" choices={channelStateChoices} />
            <UserReferenceInput sameOrg />
          </Stack>
          <TextInput source="url" style={{ width: 640 }} disabled />
          <AutocompleteArrayInput
            source="tags"
            choices={tagsChoices}
            style={{ width: 400 }}
          />
          <SelectInput
            source="type"
            choices={[
              { id: 'long', name: 'Long' },
              { id: 'short', name: 'Short' },
              { id: 'mixed', name: 'Mixed' },
            ]}
          />
          <DescriptionInput source="description" />
          <TextInput source="leadReferrer" />
          <NumberInput source="subscriberCount" disabled />
          <NumberInput source="videoCount" disabled />
          <NumberInput source="viewCount" disabled />

          <TextInput source="language" />
          <AutocompleteArrayInput
            disabled
            source="categories"
            choices={[
              { id: 'Pet', name: 'Pet' },
              { id: 'Health', name: 'Health' },
              { id: 'Simulation_video_game', name: 'Simulation Video Game' },
              { id: 'Military', name: 'Military' },
              { id: 'Pop_music', name: 'Pop Music' },
              { id: 'asdf', name: 'asdf' },
              { id: 'Motorsport', name: 'Motorsport' },
              { id: 'Association_football', name: 'Association Football' },
              { id: 'Hip_hop_music', name: 'Hip Hop Music' },
              { id: 'Casual_game', name: 'Casual Game' },
              { id: 'Business', name: 'Business' },
              { id: 'Sports_game', name: 'Sports Game' },
              { id: 'Tourism', name: 'Tourism' },
              { id: 'Role-playing_video_game', name: 'Role-playing Video Game' },
              { id: '234', name: '234' },
              { id: 'Basketball', name: 'Basketball' },
              { id: 'Knowledge', name: 'Knowledge' },
              { id: 'Politics', name: 'Politics' },
              { id: 'Television_program', name: 'Television Program' },
              { id: 'Puzzle_video_game', name: 'Puzzle Video Game' },
              { id: 'Electronic_music', name: 'Electronic Music' },
              { id: 'Strategy_video_game', name: 'Strategy Video Game' },
              { id: 'Tennis', name: 'Tennis' },
              { id: 'Jazz', name: 'Jazz' },
              { id: 'Boxing', name: 'Boxing' },
              { id: 'Racing_video_game', name: 'Racing Video Game' },
              { id: 'Music', name: 'Music' },
              { id: 'Classical_music', name: 'Classical Music' },
              { id: 'Music_of_Latin_America', name: 'Music of Latin America' },
              { id: 'Religion', name: 'Religion' },
              { id: 'Vehicle', name: 'Vehicle' },
              { id: 'Entertainment', name: 'Entertainment' },
              { id: 'Independent_music', name: 'Independent Music' },
              { id: 'Performing_arts', name: 'Performing Arts' },
              { id: 'Technology', name: 'Technology' },
              { id: 'Music_of_Asia', name: 'Music of Asia' },
              { id: 'Hobby', name: 'Hobby' },
              { id: 'Mixed_martial_arts', name: 'Mixed Martial Arts' },
              { id: 'Fashion', name: 'Fashion' },
              { id: 'Film', name: 'Film' },
              { id: 'Professional_wrestling', name: 'Professional Wrestling' },
              { id: 'Rock_music', name: 'Rock Music' },
              { id: 'Soul_music', name: 'Soul Music' },
              { id: 'Video_game_culture', name: 'Video Game Culture' },
              { id: 'Action-adventure_game', name: 'Action-adventure Game' },
              { id: 'Physical_fitness', name: 'Physical Fitness' },
              { id: 'Music_video_game', name: 'Music Video Game' },
              { id: 'Action_game', name: 'Action Game' },
              { id: 'Food', name: 'Food' },
              { id: 'Lifestyle_(sociology)', name: 'Lifestyle (Sociology)' },
              { id: 'Sport', name: 'Sport' },
              { id: 'Humour', name: 'Humour' },
              { id: 'Society', name: 'Society' },
            ]}
            style={{ width: 400 }}
          />
          {!ORG_ID && <OrganizationReferenceInput />}
          <SelectInput
            source="adSenseId"
            choices={adSenseOptions}
            alwaysOn
          />

          <UTMField />
          <CustomDateInput
            source="remindAt"
            label="Remind At"
            sx={{ width: 200, mb: 4 }}
          />
          <NullableBooleanInput source="regularCheckRequired" />
          {/* <BooleanInput source="forSale" /> */}
          {/* <BooleanInput source="specialSale" /> */}
          <TextInput source="instagramUrl" />
          {/* {
            hasRole(Role.ORG_SUPER_ADMIN) && (
              <BooleanInput source="isMuted" />
            )
          } */}
        </FormTab>
        {
          hasRole(Role.FINANCIAL_EDIT) && (
            <FormTab label="Income Sharing">
              <ArrayInput source="incomeSharing">
                <SimpleFormIterator inline>
                  <UserReferenceInput />
                  <NumberInput source="percentage" helperText={false} />
                  <TextInput source="description" />
                  <CustomDateInput
                    source="expireAt"
                    label="Expire At"
                    sx={{ width: 200 }}
                  />
                </SimpleFormIterator>
              </ArrayInput>
            </FormTab>
          )
        }
        {/* <FormTab label="Ads">
          <NumberInput source="adsPrices.long" />
          <NumberInput source="adsPrices.short" />
        </FormTab> */}
        {
          hasRole(Role.ORG_SUPER_ADMIN) && (
            <FormTab label="History">
              <ActivityTabContent />
            </FormTab>
          )
        }
        {
          hasRole(Role.INCOME_VIEW) && (
            <FormTab label="Incomes">
              <IncomeTabContent />
            </FormTab>
          )
        }
        <FormTab label="Stats">
          <StatsTabContent />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}
export function ChannelCreate(props) {
  return (
    <Create mutationMode="optimistic" {...props}>
      <TabbedForm>
        <FormTab label="url">
          <TextInput source="id" disabled />
          <TextInput source="ownerName" />
          <TextInput source="ownerTel" />
          <TextInput source="ownerEmail" />
          <TextInput source="url" />
          <TextInput source="leadSource" />
          <NumberInput source="subscriberCount" />
          <NumberInput source="videoCount" />
          <NumberInput source="viewCount" />
          <SelectInput source="state" choices={channelStateChoices} />
          {!ORG_ID && <OrganizationReferenceInput />}
          <UserReferenceInput sameOrg />
          <SelectInput
            source="adSenseId"
            choices={adSenseOptions}
            alwaysOn
          />
        </FormTab>
      </TabbedForm>
    </Create>
  );
}

function ActivityTabContent(props) {
  return (
    <ReferenceManyField
      {...props}
      perPage={100}
      reference="activities"
      target="recordId"
      addLabel={false}
      filter={{ recordModel: 'Channel' }}
    >
      <ActivitiesListContent />
    </ReferenceManyField>
  );
}
export function ChannelShow(props) {
  const hasRole = useHasRole();
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="General">
          <CopyField source="id" label="ID" />
          <CopyField source="title" />
          <TextField source="ownerName" />
          <TextField source="ownerTel" />
          <TextField source="ownerEmail" />
          <TextField source="url" />
          <TextField source="leadReferrer" />
          <NumberField source="subscriberCount" />
          <NumberField source="videoCount" />
          <NumberField source="viewCount" />
          <StateField source="state" />
          <TextField source="description" />
          <UserReferenceField source="userId" />
          <TextField source="language" />
          <CategoriesField />
          <TextField
            source="adSenseId"
            choices={adSenseOptions}
          />
          <UTMField />
          <DateField source="remindAt" />
        </Tab>
        {
          hasRole(Role.FINANCIAL_EDIT) && (
            <Tab label="Incomes">
              <ArrayField source="incomeSharing">
                <Datagrid>
                  <UserReferenceField />
                  <NumberField source="percentage" />
                  <TextField source="description" />
                  <DateField source="expireAt" />
                </Datagrid>
              </ArrayField>

              <IncomeTabContent />
            </Tab>
          )
        }
        {
          hasRole(Role.ORG_SUPER_ADMIN) && (
            <Tab label="History">
              <ActivityTabContent />
            </Tab>
          )
        }
        {
          hasRole(Role.INCOME_VIEW) && (
            <Tab label="Incomes">
              <IncomeTabContent />
            </Tab>
          )
        }
        <Tab label="Stats">
          <StatsTabContent />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
}

const channelStateChoices = generateSelectOptions(ChannelState, false, StateIconMap);

export const renderChannelResource = (roles) => (
  <Resource
    name="channels"
    show={ChannelShow}
    list={ChannelList}
    edit={rolesMatch({ roles, role: Role.CHANNEL_EDIT }) && ChannelEdit}
    create={rolesMatch({ roles, role: Role.CHANNEL_CREATE }) && ChannelCreate}
    icon={SubscriptionsRounded}
  />
);

function IncomeTabContent(props) {
  return (
    <ReferenceManyField
      {...props}
      perPage={100}
      reference="incomes"
      target="channelId"
      addLabel={false}
    >
      <IncomeListContent />
    </ReferenceManyField>
  );
}

const adSenseOptions = [
  {
    id: '3041134335536247',
    name: '3041134335536247',
  },
  {
    id: '3280661369437693',
    name: '3280661369437693',
  },
  {
    id: '4535316852950227',
    name: '4535316852950227',
  },
  {
    id: '9738256357570364',
    name: '9738256357570364',
  },
  {
    id: '7717229326906583',
    name: '7717229326906583',
  },
  {
    id: '6397070256551068',
    name: '6397070256551068',
  },
];
